/**
 * Source Citation Component
 *
 * Displays source attribution for RAG responses.
 */

import React from 'react';
import { View, Text, Pressable } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';
import { FileText, MessageSquare, User, BarChart3, ChevronDown, ChevronUp, ExternalLink, X } from 'lucide-react-native';
import { cn } from '@/lib/cn';
import type { DDSource } from '@/lib/dd-chat/assistant.types';
import AnimatedPressable from '@/components/AnimatedPressable';

interface SourceCitationProps {
  sources: DDSource[];
  expanded?: boolean;
  onToggle?: () => void;
  maxVisible?: number;
  className?: string;
}

const SOURCE_ICONS = {
  document: FileText,
  history: MessageSquare,
  profile: User,
  score: BarChart3,
};

const SOURCE_COLORS = {
  document: 'bg-blue-500/10 text-blue-600 border-blue-200',
  history: 'bg-purple-500/10 text-purple-600 border-purple-200',
  profile: 'bg-green-500/10 text-green-600 border-green-200',
  score: 'bg-amber-500/10 text-amber-600 border-amber-200',
};

export function SourceCitation({
  sources,
  expanded = false,
  onToggle,
  maxVisible = 3,
  className,
}: SourceCitationProps) {
  if (!sources || sources.length === 0) return null;

  const visibleSources = expanded ? sources : sources.slice(0, maxVisible);
  const hasMore = sources.length > maxVisible;

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      className={cn('mt-2', className)}
    >
      {/* Header */}
      <View className="flex-row items-center gap-1 mb-1.5">
        <Text className="text-xs text-zinc-500 font-medium">
          Sources ({sources.length})
        </Text>
        {hasMore && (
          <AnimatedPressable
            onPress={onToggle}
            className="flex-row items-center"
            hitSlop={8}
          >
            <Text className="text-xs text-blue-600">
              {expanded ? 'Show less' : `+${sources.length - maxVisible} more`}
            </Text>
            {expanded ? (
              <ChevronUp size={12} className="text-blue-600 ml-0.5" />
            ) : (
              <ChevronDown size={12} className="text-blue-600 ml-0.5" />
            )}
          </AnimatedPressable>
        )}
      </View>

      {/* Source Badges */}
      <View className="flex-row flex-wrap gap-1.5">
        {visibleSources.map((source, index) => (
          <SourceBadge key={`${source.referenceId}-${index}`} source={source} />
        ))}
      </View>
    </Animated.View>
  );
}

interface SourceBadgeProps {
  source: DDSource;
  onPress?: () => void;
}

export function SourceBadge({ source, onPress }: SourceBadgeProps) {
  const Icon = SOURCE_ICONS[source.type] || FileText;
  const colorClass = SOURCE_COLORS[source.type] || SOURCE_COLORS.document;
  const Container = onPress ? AnimatedPressable : View;

  return (
    <Container
      onPress={onPress}
      className={cn(
        'flex-row items-center gap-1 px-2 py-1 rounded-full border',
        colorClass
      )}
    >
      <Icon size={12} />
      <Text className="text-xs font-medium" numberOfLines={1}>
        {source.label}
      </Text>
      {source.location && (
        <Text className="text-xs opacity-70">
          · {source.location}
        </Text>
      )}
      {source.relevance !== undefined && source.relevance < 1 && (
        <Text className="text-xs opacity-50">
          {Math.round(source.relevance * 100)}%
        </Text>
      )}
    </Container>
  );
}

interface SourceDetailProps {
  source: DDSource;
  onClose?: () => void;
}

/** Dark-theme icon color map for SourceDetail */
const SOURCE_DETAIL_ICON_COLORS: Record<string, string> = {
  document: '#60a5fa', // blue-400
  history: '#c084fc',  // purple-400
  profile: '#4ade80',  // green-400
  score: '#fbbf24',    // amber-400
};

export function SourceDetail({ source, onClose }: SourceDetailProps) {
  const Icon = SOURCE_ICONS[source.type] || FileText;
  const iconColor = SOURCE_DETAIL_ICON_COLORS[source.type] ?? '#60a5fa';
  const bgTint = source.type === 'document'
    ? 'bg-blue-500/15'
    : source.type === 'history'
      ? 'bg-purple-500/15'
      : source.type === 'profile'
        ? 'bg-green-500/15'
        : 'bg-amber-500/15';

  return (
    <Animated.View
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(150)}
      className="bg-zinc-800 rounded-lg border border-zinc-700/60 p-3"
    >
      {/* Header */}
      <View className="flex-row items-center justify-between mb-2">
        <View className="flex-row items-center gap-2 flex-1">
          <View className={cn('p-1.5 rounded-full', bgTint)}>
            <Icon size={14} color={iconColor} />
          </View>
          <View className="flex-1">
            <Text
              className="text-sm font-semibold text-zinc-200"
              numberOfLines={1}
            >
              {source.label}
            </Text>
            {source.location && (
              <Text className="text-xs text-zinc-500">{source.location}</Text>
            )}
          </View>
        </View>
        {onClose && (
          <Pressable onPress={onClose} hitSlop={8} className="ml-2">
            <X size={14} color="#71717a" />
          </Pressable>
        )}
      </View>

      {/* Snippet */}
      {source.snippet && (
        <View className="bg-zinc-900/60 rounded p-2 mt-1">
          <Text className="text-xs text-zinc-400 leading-relaxed">
            "{source.snippet}"
          </Text>
        </View>
      )}

      {/* Relevance */}
      {source.relevance !== undefined && (
        <View className="flex-row items-center mt-2">
          <Text className="text-xs text-zinc-500">Relevance: </Text>
          <View className="flex-1 h-1 bg-zinc-700 rounded-full ml-1">
            <View
              className="h-full bg-blue-500 rounded-full"
              style={{ width: `${source.relevance * 100}%` }}
            />
          </View>
          <Text className="text-xs text-zinc-400 ml-1">
            {Math.round(source.relevance * 100)}%
          </Text>
        </View>
      )}

      {/* Open Document action — shown when referenceId exists (future deep link) */}
      {source.referenceId && (
        <AnimatedPressable
          className="flex-row items-center gap-1.5 mt-2.5 pt-2 border-t border-zinc-700/40"
        >
          <ExternalLink size={12} color="#60a5fa" />
          <Text className="text-xs text-blue-400 font-medium">
            Open Document
          </Text>
        </AnimatedPressable>
      )}
    </Animated.View>
  );
}

export default SourceCitation;
